/**
 * FEEDBACK (C1) — UM componente de campo para os três formulários: a autoavaliação (link público), a avaliação do
 * gestor e a pauta. Desenha cada tipo do sistema antigo (`anexos/feedback-conteudo.json`) com os componentes canônicos:
 * texto (`Input`), texto longo (mesmo visual do `Input`), lista (`Select`), escolha única (botões com alvo ≥ 44px,
 * inclusive a escala Alto/Médio/Baixo) e múltipla escolha (`Checkbox`, com os grupos de etapas).
 *
 * Gravação: texto grava ao SAIR do campo (`aoConfirmar` no blur); escolha grava na hora. Todo texto exibido vem da
 * pergunta (literal) — nenhum rótulo nasce aqui.
 */
import { useEffect, useState } from 'react';
import { Checkbox, Input, Label, Select, SelectOption } from '@/components/ui';
import { cn } from '@/lib/cn';
import type { PerguntaDoFormulario } from '@/lib/feedback/feedback-store';
import { NIVEIS } from '@/lib/feedback/quadrante';

type Valor = string | string[] | null;

const CAIXA =
  'w-full rounded-xl bg-paper-surface dark:bg-paper-dark-surface text-ink-1 dark:text-ink-dark-1 placeholder-ink-3 border border-line ' +
  'dark:border-line-dark focus:outline-none focus:ring-2 focus:border-red focus:ring-red-soft dark:focus:ring-red-soft-dark transition-colors px-4 py-3 text-sm';

const opcoesDe = (p: PerguntaDoFormulario): Array<{ valor: string; rotulo: string; descricao?: string | null }> => {
  if (p.escala) return NIVEIS.map((n) => ({ valor: n, rotulo: n }));
  return (p.opcoes ?? []).map((o) => (typeof o === 'string' ? { valor: o, rotulo: o } : { valor: o.valor, rotulo: o.rotulo, descricao: o.descricao }));
};

export interface CampoProps {
  pergunta: PerguntaDoFormulario & { rotulo?: string };
  valor: Valor | undefined;
  /** Grava (texto: ao sair do campo; escolha: na hora). */
  aoConfirmar: (v: Valor) => void;
  somenteLeitura?: boolean;
  /** Formulário com botão de salvar (avaliação, pauta): o texto vai para o rascunho a cada tecla, não só ao sair. */
  aCadaTecla?: boolean;
  erro?: string | null;
}

export function CampoDoFormulario({ pergunta: p, valor, aoConfirmar, somenteLeitura, erro, aCadaTecla }: CampoProps) {
  const id = `campo-${p.id}`;
  const rotulo = p.texto ?? p.rotulo ?? '';
  const opcional = (p as { marcador_opcional?: string }).marcador_opcional;
  // Obrigatória = o `required` NATIVO, como no sistema antigo: o navegador avisa no próprio campo e põe o foco nele
  // (nenhum texto novo). `obrigatoria` condicional ("so quando...") não conta. As obrigatórias são todas texto/lista.
  const obrigatoria = p.obrigatoria === true;
  const [texto, setTexto] = useState(typeof valor === 'string' ? valor : '');
  useEffect(() => setTexto(typeof valor === 'string' ? valor : ''), [valor]);
  const digitar = (v: string) => {
    setTexto(v);
    if (aCadaTecla) aoConfirmar(v);
  };

  const cabecalho = rotulo ? (
    <Label htmlFor={p.tipo === 'radio' || p.tipo === 'checkbox' ? undefined : id} className="block text-sm font-medium text-ink-1 dark:text-ink-dark-1">
      {rotulo}
      {opcional && <span className="ml-1 font-normal text-ink-3 dark:text-ink-dark-3">{opcional}</span>}
    </Label>
  ) : null;
  const ajuda = p.ajuda ? <p className="text-xs text-ink-3 dark:text-ink-dark-3">{p.ajuda}</p> : null;
  const erroDoCampo = erro ? <p className="text-sm text-red" role="alert">{erro}</p> : null;

  if (somenteLeitura) {
    const v = Array.isArray(valor) ? valor.join('; ') : (valor ?? '');
    if (!v) return null;
    return (
      <div className="space-y-1" data-testid={`leitura-${p.id}`}>
        {cabecalho}
        <p className="whitespace-pre-line text-sm text-ink-1 dark:text-ink-dark-1">{v}</p>
      </div>
    );
  }

  if (p.tipo === 'radio') {
    const atual = typeof valor === 'string' ? valor : '';
    return (
      <fieldset className="space-y-2" data-testid={`campo-${p.id}`}>
        {rotulo && <legend className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">{rotulo}</legend>}
        {ajuda}
        <div role="radiogroup" aria-label={rotulo} className="flex flex-wrap gap-2">
          {opcoesDe(p).map((o) => (
            <button
              key={o.valor}
              type="button"
              role="radio"
              aria-checked={atual === o.valor}
              onClick={() => aoConfirmar(o.valor)}
              data-testid={`opcao-${p.id}-${o.valor}`}
              className={cn(
                'min-h-[44px] rounded-xl border px-4 text-sm font-medium transition-colors',
                atual === o.valor
                  ? 'border-red bg-red-soft text-red-deep dark:bg-red-soft-dark dark:text-red-glow'
                  : 'border-line bg-paper-surface text-ink-1 hover:border-ink-3 dark:border-line-dark dark:bg-paper-dark-surface dark:text-ink-dark-1',
              )}
            >
              {o.rotulo}
            </button>
          ))}
        </div>
        {erroDoCampo}
      </fieldset>
    );
  }

  if (p.tipo === 'checkbox') {
    const marcados = Array.isArray(valor) ? valor : [];
    const alternar = (v: string) => aoConfirmar(marcados.includes(v) ? marcados.filter((x) => x !== v) : [...marcados, v]);
    const grupos = p.grupos ?? [{ departamento: null, etapas: opcoesDe(p).map((o) => o.valor) }];
    const rotuloDe = new Map(opcoesDe(p).map((o) => [o.valor, o.rotulo]));
    const descricaoDe = new Map(opcoesDe(p).map((o) => [o.valor, o.descricao ?? null]));
    return (
      <fieldset className="space-y-3" data-testid={`campo-${p.id}`}>
        {rotulo && <legend className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">{rotulo}</legend>}
        {ajuda}
        {grupos.map((g, gi) => (
          <div key={gi} className="space-y-1">
            {g.departamento && <p className="text-xs font-semibold uppercase tracking-[0.06em] text-ink-3 dark:text-ink-dark-3">{g.departamento}</p>}
            {g.etapas.map((v) => (
              <label key={v} className="flex min-h-[44px] cursor-pointer items-start gap-3 py-2 text-sm text-ink-1 dark:text-ink-dark-1">
                <Checkbox checked={marcados.includes(v)} onChange={() => alternar(v)} aria-label={rotuloDe.get(v) ?? v} />
                <span>
                  <span className="block">{rotuloDe.get(v) ?? v}</span>
                  {descricaoDe.get(v) && <span className="block text-xs text-ink-3 dark:text-ink-dark-3">{descricaoDe.get(v)}</span>}
                </span>
              </label>
            ))}
          </div>
        ))}
        {erroDoCampo}
      </fieldset>
    );
  }

  if (p.tipo === 'select') {
    return (
      <div className="space-y-1" data-testid={`campo-${p.id}`}>
        {cabecalho}
        <Select id={id} required={obrigatoria} value={typeof valor === 'string' ? valor : ''} onChange={(e) => aoConfirmar(e.target.value || null)} wrapperClassName="w-full">
          <SelectOption value="">{p.placeholder ?? '—'}</SelectOption>
          {opcoesDe(p).map((o) => (
            <SelectOption key={o.valor} value={o.valor}>
              {o.rotulo}
            </SelectOption>
          ))}
        </Select>
        {erroDoCampo}
      </div>
    );
  }

  if (p.tipo === 'textarea') {
    return (
      <div className="space-y-1" data-testid={`campo-${p.id}`}>
        {cabecalho}
        {ajuda}
        <textarea
          id={id}
          value={texto}
          rows={4}
          required={obrigatoria}
          maxLength={p.maxlength}
          placeholder={p.placeholder}
          aria-invalid={erro ? true : undefined}
          onChange={(e) => digitar(e.target.value)}
          onBlur={() => texto !== (valor ?? '') && aoConfirmar(texto)}
          className={cn(CAIXA, 'min-h-[112px] resize-y', erro && 'border-red')}
        />
        {erroDoCampo}
      </div>
    );
  }

  // text, date
  return (
    <div className="space-y-1" data-testid={`campo-${p.id}`}>
      {cabecalho}
      {ajuda}
      <Input
        id={id}
        type={p.tipo === 'date' ? 'date' : 'text'}
        required={obrigatoria}
        value={texto}
        placeholder={p.placeholder}
        maxLength={p.maxlength}
        aria-invalid={erro ? true : undefined}
        onChange={(e) => digitar(e.target.value)}
        onBlur={() => texto !== (valor ?? '') && aoConfirmar(texto)}
      />
      {erroDoCampo}
    </div>
  );
}
